import React, { lazy, Suspense, useEffect } from 'react';
import Navigation from '../components/Navigation';
import HeroSection from '../components/HeroSection';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { organizationSchema, websiteSchema, courseSchema } from '../utils/structuredData';

// Lazy load non-critical components for better performance
const SolutionSection = lazy(() => import('../components/SolutionSection'));
const ProductShowcase = lazy(() => import('../components/ProductShowcase'));
const SocialProof = lazy(() => import('../components/SocialProof'));

const HomePage = () => {
  // Enhanced performance tracking and accessibility setup
  useEffect(() => {
    // Report Core Web Vitals
    if ('web-vital' in window) {
      window.webVitals?.getCLS(console.log);
      window.webVitals?.getFID(console.log);
      window.webVitals?.getFCP(console.log);
      window.webVitals?.getLCP(console.log);
      window.webVitals?.getTTFB(console.log);
    }

    // Enhanced keyboard navigation for accessibility
    const handleKeyDown = (e) => {
      // Skip to main content on keyboard navigation
      if (e.ctrlKey || e.metaKey) return;
      
      // Escape key to close any open modals or menus
      if (e.code === 'Escape') {
        const focusedElement = document.activeElement;
        focusedElement?.blur();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Enhanced structured data for homepage
  const homePageSchema = [
    {
      ...organizationSchema,
      // Update URLs to match actual domain
      url: "https://emberimpact.com",
      logo: "https://customer-assets.emergentagent.com/job_ember-ui-update/artifacts/y5fpxlpp_Ember-Logo-website.png",
      sameAs: [
        "https://www.linkedin.com/company/ember-impact-ventures",
        "https://twitter.com/EmberLeadership"
      ],
      description: "EMBER Impact Ventures transforms leaders through authentic community, research-backed practices, and innovative programs including The Crucible peer accelerator, Spark Moments, and LIT Expeditions.",
      email: "hello@emberimpact.com"
    },
    {
      ...websiteSchema,
      // Update to match actual domain
      url: "https://emberimpact.com",
      potentialAction: {
        "@type": "SearchAction",
        "target": "https://emberimpact.com/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    },
    courseSchema({
      name: "The Crucible: Peer Leadership Accelerator™",
      description: "Research-validated 8-week peer leadership program. Develop Emotional Intelligence, Personal Agency & Authentic Connection with fellow leaders.",
      mode: "online",
      duration: "P8W",
      price: "1997",
      startDate: "2025-10-18",
      applicationDeadline: "2025-10-11"
    })
  ];

  return (
    <>
      <SEOHead 
        title="EMBER | Leadership Development & Innovation Frameworks"
        description="Develop exceptional leadership through fire-tested frameworks, peer accelerators, and transformative experiences. For leaders ready to navigate uncertainty and create lasting impact."
        keywords="leadership development, leadership training, authentic leadership, executive coaching, leadership transformation, peer accelerator, leadership community, professional development, innovation frameworks, transformative experiences, leadership uncertainty"
        ogImage="https://customer-assets.emergentagent.com/job_ember-ui-update/artifacts/y5fpxlpp_Ember-Logo-website.png"
        canonicalUrl="/"
        structuredData={homePageSchema}
      />
      
      {/* Preconnect for performance optimization */}
      <link rel="preconnect" href="https://images.unsplash.com" />
      <link rel="preconnect" href="https://fonts.googleapis.com" crossOrigin="" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      
      {/* Enhanced Skip Links for WCAG AA Compliance */}
      <a 
        href="#main-content" 
        className="skip-nav"
        style={{
          position: 'absolute',
          left: '-10000px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
        onFocus={(e) => {
          e.target.style.position = 'absolute';
          e.target.style.left = '6px';
          e.target.style.top = '7px';
          e.target.style.width = 'auto';
          e.target.style.height = 'auto';
          e.target.style.overflow = 'visible';
          e.target.style.zIndex = '999999';
          e.target.style.padding = '8px 16px';
          e.target.style.background = '#FF5722';
          e.target.style.color = '#fff';
          e.target.style.textDecoration = 'none';
          e.target.style.borderRadius = '4px';
          e.target.style.fontWeight = '600';
        }}
        onBlur={(e) => {
          e.target.style.position = 'absolute';
          e.target.style.left = '-10000px';
          e.target.style.top = 'auto';
          e.target.style.width = '1px';
          e.target.style.height = '1px';
          e.target.style.overflow = 'hidden';
        }}
      >
        Skip to main content
      </a>
      <a 
        href="#ignite-transformation" 
        className="skip-nav"
        style={{
          position: 'absolute',
          left: '-10000px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
        onFocus={(e) => {
          e.target.style.position = 'absolute';
          e.target.style.left = '200px';
          e.target.style.top = '7px';
          e.target.style.width = 'auto';
          e.target.style.height = 'auto';
          e.target.style.overflow = 'visible';
          e.target.style.zIndex = '999999';
          e.target.style.padding = '8px 16px';
          e.target.style.background = '#FF5722';
          e.target.style.color = '#fff';
          e.target.style.textDecoration = 'none';
          e.target.style.borderRadius = '4px';
          e.target.style.fontWeight = '600';
        }}
        onBlur={(e) => {
          e.target.style.position = 'absolute';
          e.target.style.left = '-10000px';
          e.target.style.top = 'auto';
          e.target.style.width = '1px';
          e.target.style.height = '1px';
          e.target.style.overflow = 'hidden';
        }}
      >
        Skip to programs
      </a>

      {/* Enhanced Performance and Accessibility Styles - Mobile First */}
      <style>{`
        /* Mobile-first CSS Variables */
        :root {
          --ember-fire: #FF5722;
          --ember-flame: #FF7043;
          --ember-heat: #FF6F00;  
          --ember-spark: #FFB74D;
          --ember-glow: #FFCC80;
          --ember-depth: #673AB7;
          --text-primary: #212121;
          --text-secondary: #455A64;
          --neutral-warm: #FFF8F0;
          --neutral-light: #F5F5F5;
          --neutral-medium: #757575;
          --neutral-dark: #424242;
          --neutral-charcoal: #263238;
        }
        
        /* Mobile-first reset */
        * {
          box-sizing: border-box;
        }
        
        /* Enhanced focus management - WCAG AAA */
        *:focus {
          outline: 3px solid #FFC107 !important;
          outline-offset: 2px !important;
          border-radius: 2px;
        }
        
        /* Mobile-first container */
        .homepage-container { 
          contain: layout style paint;
          will-change: transform;
          min-height: 100vh;
        }
        
        /* Loading placeholder with accessibility */
        .loading-placeholder {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: loading 1.5s infinite;
          border-radius: 8px;
          position: relative;
        }
        
        .loading-placeholder::after {
          content: 'Loading content...';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: var(--text-secondary);
          font-size: 14px;
          font-weight: 500;
        }
        
        @keyframes loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        
        /* Mobile-first section spacing */
        .section-divider { 
          margin: 1rem 0; 
          height: 1px;
          background: transparent;
        }
        
        /* Mobile-first typography */
        h1, h2, h3 { 
          line-height: 1.2;
          margin-bottom: 1rem;
        }
        
        h1 {
          font-size: 1.75rem; /* 28px */
        }
        
        h2 {
          font-size: 1.5rem; /* 24px */
        }
        
        h3 {
          font-size: 1.25rem; /* 20px */
        }
        
        /* Performance optimizations */
        img {
          content-visibility: auto;
          contain-intrinsic-size: 400px 300px;
          loading: lazy;
        }
        
        section {
          contain: layout style paint;
        }
        
        /* Mobile-first button styles */
        .btn-primary {
          background: var(--ember-fire);
          color: white;
          border: none;
          border-radius: 6px;
          padding: 12px 24px;
          font-weight: 600;
          font-size: 16px;
          min-height: 44px;
          min-width: 44px;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        
        .btn-primary:hover,
        .btn-primary:focus {
          background: #E64A19;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(255, 87, 34, 0.3);
        }
        
        .btn-primary:active {
          transform: translateY(0);
        }
        
        /* Tablet styles - 640px and up */
        @media (min-width: 640px) {
          .section-divider { 
            margin: 2rem 0; 
          }
          
          h1 {
            font-size: 2.25rem; /* 36px */
          }
          
          h2 {
            font-size: 1.875rem; /* 30px */
          }
          
          h3 {
            font-size: 1.5rem; /* 24px */
          }
          
          .btn-primary {
            padding: 16px 32px;
            font-size: 18px;
          }
        }
        
        /* Desktop styles - 1024px and up */
        @media (min-width: 1024px) {
          .section-divider { 
            margin: 3rem 0; 
          }
          
          h1 {
            font-size: 3rem; /* 48px */
          }
          
          h2 {
            font-size: 2.25rem; /* 36px */
          }
          
          h3 {
            font-size: 1.875rem; /* 30px */
          }
        }
        
        /* Large desktop - 1440px and up */
        @media (min-width: 1440px) {
          h1 {
            font-size: 4rem; /* 64px */
          }
        }
        
        /* High contrast mode support - WCAG AAA */
        @media (prefers-contrast: high) {
          :root {
            --text-primary: #000000;
            --text-secondary: #000000;
            --ember-fire: #CC0000;
          }
          
          button, input, a {
            border: 2px solid currentColor !important;
          }
          
          .btn-primary {
            background: #000000 !important;
            color: #FFFFFF !important;
            border: 2px solid #FFFFFF !important;
          }
        }
        
        /* Reduced motion support - WCAG AAA */
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
          
          .loading-placeholder {
            animation: none !important;
            background: #f0f0f0 !important;
          }
        }
        
        /* Print styles */
        @media print {
          .skip-nav,
          .btn-primary {
            display: none !important;
          }
          
          body {
            background: white !important;
            color: black !important;
          }
          
          * {
            background: white !important;
            color: black !important;
          }
        }
        
        /* Screen reader only content */
        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }
        
        /* Focus visible for modern browsers */
        .btn-primary:focus-visible {
          outline: 3px solid #FFC107;
          outline-offset: 3px;
        }
        
        /* Enhanced error handling */
        .error-boundary {
          padding: 2rem;
          text-align: center;
          background: #FFF3E0;
          border: 1px solid #FFB74D;
          border-radius: 8px;
          margin: 1rem;
        }
        
        .error-boundary h2 {
          color: var(--ember-fire);
          margin-bottom: 1rem;
        }
        
        .error-boundary p {
          color: var(--text-secondary);
          margin-bottom: 1rem;
        }
      `}</style>

      <Navigation />
      
      <main 
        id="main-content" 
        role="main" 
        aria-label="EMBER Leadership Development homepage content"
      >
          <HeroSection />
          <div className="section-divider" role="presentation"></div>
          
          <Suspense fallback={
            <div className="loading-placeholder h-96 mx-auto max-w-6xl rounded-lg m-8" aria-label="Loading page content">
              <span className="sr-only">Loading leadership development content...</span>
            </div>
          }>
            <section id="ignite-transformation" aria-label="Leadership transformation programs">
              <SolutionSection />
              <ProductShowcase />
              <SocialProof />
            </section>
          </Suspense>
        </main>
        
        <Footer />
    </>
  );
};

export default HomePage;